// Scored "Pick X" verdict block. Pure presentational; caller computes the
// `ComputedTotals` via `computeTotals(...)` so this can be a server component.

import type { ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { GrassTuft } from '@/components/icons/grass-tuft'
import { GrassCharacteristics } from '@/lib/types/recommendation-engine'
import { GRASS_COMPARISON_TRAITS, ComputedTotals } from '@/lib/grass-comparison-scoring'
import { getGrassComparisonUi } from '@/data/grass-comparison-ui'
import { cn } from '@/lib/utils'

interface VerdictCardProps {
  grasses: GrassCharacteristics[]
  totals: ComputedTotals
  /** Region label resolved from the ZIP, e.g. "Southeast". Pass null when no ZIP. */
  region: string | null
  /** Optional header action (the share button), rendered top-right. */
  action?: ReactNode
}

export function VerdictCard({ grasses, totals, region, action }: VerdictCardProps) {
  if (grasses.length === 0) return null

  if (totals.noClimateCompatibleWinner && region && totals.climateSeason) {
    const subject = grasses.length === 2 ? 'Neither choice' : 'None of these grasses'
    return (
      <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm sm:p-6">
        <div className="flex items-start gap-3">
          <span
            className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700"
            aria-hidden="true"
          >
            <AlertTriangle className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-bold leading-tight tracking-tight text-gray-900 sm:text-2xl">
              {subject} fits {region}.
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-gray-700">
              {region} has a {totals.climateSeason.toLowerCase()}-season climate. Compare a{' '}
              {totals.climateSeason.toLowerCase()}-season grass before buying seed, sod, or plugs.
            </p>
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      </section>
    )
  }

  if (totals.winnerIdx < 0) return null
  const champion = grasses[totals.winnerIdx]
  const championUi = champion ? getGrassComparisonUi(champion.name) : null

  const regionChip = !totals.tied && region ? `Best fit for ${region}` : null

  return (
    <section className="tool-peak-card overflow-hidden">
      <div className="border-b border-green-100 p-4 sm:flex sm:items-center sm:gap-4 sm:p-6">
        <div className="flex min-w-0 items-start gap-3 sm:flex-1 sm:items-center">
          <span
            className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-green-50 text-green-700"
            aria-hidden="true"
          >
            <GrassTuft className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-bold leading-tight tracking-tight text-gray-900 sm:text-2xl">
              {totals.tied ? (
                <>
                  It&apos;s a draw:{' '}
                  <span className="text-gray-700">
                    {grasses.map((g) => g.name).join(' and ')}
                  </span>{' '}
                  score even.
                </>
              ) : (
                <>
                  Pick{' '}
                  <span
                    className="border-b-2 pb-0.5"
                    style={{
                      color: championUi?.accentColor ?? '#16a34a',
                      borderColor: championUi?.accentColor ?? '#16a34a',
                    }}
                  >
                    {champion?.name}
                  </span>
                  .
                </>
              )}
            </h2>
            {regionChip && (
              <span className="mt-1.5 inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                {regionChip}
              </span>
            )}
          </div>
        </div>
        {(!totals.tied || action) && (
          <div className="ml-14 mt-2 flex items-center gap-3 sm:ml-auto sm:mt-0">
            {!totals.tied && (
              <div className="inline-flex w-fit items-baseline gap-1 rounded-full bg-white px-3.5 py-1.5 ring-1 ring-green-100">
                <span
                  className="text-2xl font-extrabold tracking-tight tabular-nums"
                  style={{ color: championUi?.accentColor ?? '#16a34a' }}
                >
                  {totals.totals[totals.winnerIdx]}
                </span>
                <span className="text-xs text-gray-500">/ {totals.maxScore}</span>
              </div>
            )}
            {action}
          </div>
        )}
      </div>

      <ul
        className={cn(
          'grid grid-cols-1 gap-3 p-4 sm:gap-4 sm:p-6 min-[720px]:gap-4',
          grasses.length === 2 ? 'min-[720px]:grid-cols-2' : 'min-[720px]:grid-cols-3',
        )}
      >
        {grasses.map((g, i) => {
          const ui = getGrassComparisonUi(g.name)
          const isWinner = !totals.tied && i === totals.winnerIdx
          const isClimateMismatch = totals.climateCompatible[i] === false
          return (
            <li
              key={g.name}
              className="flex min-w-0 items-center gap-3 rounded-xl bg-gray-50/70 p-3 min-[720px]:rounded-none min-[720px]:bg-transparent min-[720px]:p-0"
            >
              <span
                className={cn(
                  'inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-white',
                  isWinner && 'ring-4 ring-green-100',
                )}
                style={{ backgroundColor: ui.accentColor }}
                aria-hidden="true"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M5 21h14M8 21c0-5 1-8 3-10M12 21c0-7 2-10 4-13M16 21c0-4 1-6 2-8" />
                </svg>
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5 text-sm font-semibold leading-snug text-gray-900">
                  <span className="min-w-0 break-words min-[720px]:truncate">{g.name}</span>
                  {isWinner && (
                    <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                      Winner
                    </span>
                  )}
                  {isClimateMismatch && (
                    <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
                      Climate mismatch
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  Wins {totals.wins[i]} of {GRASS_COMPARISON_TRAITS.length} traits
                  {totals.zoneBonuses[i] > 0 && (
                    <span className="font-semibold text-green-700"> · +{totals.zoneBonuses[i]} zone fit</span>
                  )}
                </p>
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
